import { useEffect, useMemo, useState } from 'react';
import { Clock } from 'lucide-react';
import { useAssignSubscription, useSubscriptionPlans } from '@features/users/model/use-users';
import {
  DEFAULT_DEMO_DAYS,
  DEMO_DURATIONS,
  DEMO_STATUS,
  demoEndDate,
  describePlanOption,
  displayName,
  type PlatformUser,
} from '@features/users/model/user.types';
import { Alert } from '@shared/ui/Alert';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';
import { Modal } from '@shared/ui/Modal';
import { Select, type SelectOption } from '@shared/ui/Select';
import { Spinner } from '@shared/ui/Spinner';
import { cn } from '@shared/lib/cn';
import { formatDate } from '@shared/lib/format-date';
import { ApiError } from '@shared/api/api-error';

interface AssignPlanDialogProps {
  /** The user being given access, or null when the dialog is closed. */
  user: PlatformUser | null;
  onClose: () => void;
  /** Called once the demo has been written. */
  onAssigned?: (user: PlatformUser) => void;
}

/** `-1` in the duration state means "an end date was typed in". */
const CUSTOM = -1;

/**
 * Grants a user temporary access to a plan so they can try the product.
 *
 * Everything here is a demo: the access is written as a trial with an end date,
 * and the API's expiry sweep closes it when that date passes. There is no
 * open-ended option on purpose — an admin-granted subscription that never ends
 * is indistinguishable from a paying customer in every figure the admin reports.
 */
export function AssignPlanDialog({ user, onClose, onAssigned }: AssignPlanDialogProps) {
  const plans = useSubscriptionPlans();
  const assign = useAssignSubscription();

  const [planId, setPlanId] = useState('');
  const [days, setDays] = useState<number>(DEFAULT_DEMO_DAYS);
  const [customEnd, setCustomEnd] = useState('');

  // Every open starts clean, so the previous user's choices cannot be submitted
  // against this one.
  useEffect(() => {
    if (user) {
      setPlanId('');
      setDays(DEFAULT_DEMO_DAYS);
      setCustomEnd('');
      assign.reset();
    }
    // `assign` is a stable mutation object; including it would re-run this on
    // every state change the mutation makes, wiping the form mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Live plans first — a retired plan can still be demoed, for showing someone
  // the arrangement they used to have, but it is not the default choice.
  const planOptions: SelectOption[] = useMemo(() => {
    const all = plans.data ?? [];

    return [
      ...all
        .filter((plan) => plan.isActive)
        .map((plan) => ({ value: plan.id, label: describePlanOption(plan), group: 'Current plans' })),
      ...all
        .filter((plan) => !plan.isActive)
        .map((plan) => ({ value: plan.id, label: describePlanOption(plan), group: 'Retired plans' })),
    ];
  }, [plans.data]);

  const endsAt = days === CUSTOM ? customEnd : demoEndDate(days);
  const error = assign.error instanceof ApiError ? assign.error : null;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!user || !planId || !endsAt) {
      return;
    }

    assign.mutate(
      { userId: user.id, planId, status: DEMO_STATUS, endsAt },
      {
        onSuccess: () => {
          onAssigned?.(user);
          onClose();
        },
      },
    );
  };

  return (
    <Modal
      open={user !== null}
      onClose={onClose}
      title="Give demo access"
      subtitle={user ? (displayName(user) ?? user.email ?? 'Unnamed user') : undefined}
      footer={
        <>
          <Button type="button" variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="assign-plan-form"
            size="sm"
            disabled={!planId || !endsAt}
            isLoading={assign.isPending}
          >
            Start demo
          </Button>
        </>
      }
    >
      <form id="assign-plan-form" onSubmit={submit} className="space-y-4">
        {error && <Alert tone="error">{error.message}</Alert>}

        {plans.isPending ? (
          <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
            <Spinner className="h-4 w-4" />
            Loading plans…
          </div>
        ) : (
          <Select
            label="Plan to demo"
            placeholder="Choose a plan"
            options={planOptions}
            value={planId}
            onChange={(event) => setPlanId(event.target.value)}
            required
          />
        )}

        <div>
          <p className="mb-1.5 block text-sm font-medium text-slate-700">How long</p>

          <div className="flex flex-wrap gap-1.5">
            {DEMO_DURATIONS.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setDays(option)}
                aria-pressed={days === option}
                className={cn(
                  'rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
                  days === option
                    ? 'border-periscope-600 bg-periscope-50 text-periscope-800'
                    : 'border-slate-300 text-slate-600 hover:bg-slate-50',
                )}
              >
                {option} days
              </button>
            ))}

            <button
              type="button"
              onClick={() => setDays(CUSTOM)}
              aria-pressed={days === CUSTOM}
              className={cn(
                'rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
                days === CUSTOM
                  ? 'border-periscope-600 bg-periscope-50 text-periscope-800'
                  : 'border-slate-300 text-slate-600 hover:bg-slate-50',
              )}
            >
              Pick a date
            </button>
          </div>

          {days === CUSTOM && (
            <div className="mt-2.5">
              <Input
                label="Ends on"
                type="date"
                value={customEnd}
                min={demoEndDate(1)}
                onChange={(event) => setCustomEnd(event.target.value)}
                required
              />
            </div>
          )}
        </div>

        <Alert tone="info">
          <span className="flex items-start gap-1.5">
            <Clock aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Access ends automatically on{' '}
              <strong>{endsAt ? formatDate(endsAt) : 'the date you pick'}</strong>. It is recorded
              as a trial, so it never counts as a paying customer.
            </span>
          </span>
        </Alert>
      </form>
    </Modal>
  );
}
